import {
  MolsonAccordion,
  MolsonBox,
  MolsonContainer,
  MolsonDxGrid,
  MolsonListSubheader
} from '@components/reusable'
import { type molsonTheme } from '@theme'
import { type molsonUseTheme } from '@theme/customMuiExports'
import { molsonStyled } from '@/theme/customMuiExports'
import { ColorizedHeaderDxGrid } from '../custom-dx-grids/CustomDxGrids'

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
export const iconTextBoxStyles = (theme: any) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.secondary[100],
  border: `2px solid ${theme.palette.secondary[200]}`,
  borderRadius: '12px',
  cursor: 'pointer',
  display: 'flex',
  flex: '1',
  flexDirection: 'column',
  height: 135,
  justifyContent: 'center',
  padding: '20px',
  userSelect: 'none'
})

export type CardType = 'default' | 'error' | 'spec'

const getCardStyles = (
  theme: ReturnType<typeof molsonUseTheme>,
  type: CardType
) => {
  const borderColors = {
    default: `1px solid ${theme.palette.secondary[400]}`,
    error: `2px solid ${theme.palette.primary.error}`,
    spec: `2px solid ${theme.palette.primary.light}`
  }

  const backgroundColors = {
    default: theme.palette.secondary[50],
    error: 'rgba(211, 238, 237, 0.20)',
    spec: 'rgba(211, 238, 237, 0.20)'
  }

  return {
    background: backgroundColors[type as keyof typeof backgroundColors] ?? '',
    border: borderColors[type as keyof typeof borderColors] ?? ''
  }
}

export const itemCardStyles = (
  theme: typeof molsonTheme,
  type: CardType = 'default'
) => ({
  alignItems: 'center',
  background: getCardStyles(theme, type).background,
  border: getCardStyles(theme, type).border,
  borderRadius: 2,
  display: 'flex',
  flexDirection: ' column',
  justifyContent: 'space-between',
  marginTop: 2.5,
  padding: '24px 36px'
})

export const LabelInputWrapper = molsonStyled(MolsonBox)({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px'
})

export const StyledGrid = molsonStyled(MolsonDxGrid)({
  '& .dx-datagrid-header-panel .dx-toolbar, .dx-datagrid-header-panel': {
    backgroundColor: 'transparent !important'
  },
  '& .dx-datagrid-headers': {
    marginTop: '-12px'
  }
})

export const CustomMolsonAccordion = molsonStyled(MolsonAccordion)(
  ({ theme, type }: { theme?: typeof molsonTheme; type?: string }) => ({
    '& .MuiAccordionSummary-content > div h4:nth-of-type(2)': {
      color: type === 'tradeIn' && theme?.palette.primary.error
    },
    '& div[role="region"] > div': {
      paddingBottom: '10px !important'
    },

    '&::after': {
      height: '0 !important'
    }
  })
)

export const LevelBlock = molsonStyled(MolsonBox)(({ theme }) => ({
  backgroundColor: theme.palette.secondary[100],
  borderRadius: 8,
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
  padding: 30
}))

export const RulesSubjectCategory = molsonStyled(MolsonListSubheader)(
  ({ theme }) => ({
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontWeight: 400,
    fontSize: '1rem',
    color: theme.palette.secondary[900]
  })
)

export const tagsInputStyles = {
  '.MuiOutlinedInput-root': {
    height: 'auto',
    minHeight: '49px',
    paddingBottom: 0.5,
    paddingTop: 0.5
  }
}

export const BulkActionsContainer = molsonStyled(MolsonBox)({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  cursor: 'pointer'
})

export const CustomDxGrid = molsonStyled(ColorizedHeaderDxGrid)(
  ({
    selectedRowsColor,
    searchPanelMarginRight = 0
  }: {
    selectedRowsColor: string
    searchPanelMarginRight?: number
  }) => ({
    '& .dx-datagrid-rowsview .dx-selection.dx-row:not(.dx-row-focused):not(.dx-row-removed) > td':
      {
        backgroundColor: selectedRowsColor
      },
    '& .dx-datagrid-search-panel': {
      marginRight: `${searchPanelMarginRight}px !important`
    }
  })
)

export const CustomContainer = molsonStyled(MolsonContainer)({
  '&.MuiContainer-maxWidthXl': {
    marginBottom: 0,
    marginTop: 0
  }
})
